import { useEffect } from "react";

const SITE_NAME = "Aeti Cleaning Services";
export const SITE_URL = "https://aeticleaningservices.com.au";

const setMetaByName = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setMetaByProperty = (property, content) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setCanonical = (href) => {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const STRUCTURED_DATA_ID = "page-structured-data";

// Builds a BreadcrumbList for a page — pass [{ name, path }] from Home down to the current page
// (path "" for Home itself). Combine with another schema object via combineStructuredData() when
// a page already has its own (e.g. Services' ItemList) — a single page should emit one <script>
// with a @graph, not two competing application/ld+json blocks.
export const buildBreadcrumbs = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const combineStructuredData = (...schemas) => ({
  "@context": "https://schema.org",
  "@graph": schemas.map(({ "@context": _drop, ...rest }) => rest),
});

// Updates document title, meta description/canonical and the Open Graph/Twitter mirrors for the
// current route. CRA has no per-route static HTML, so this is what gives each page its own
// search-result title/snippet instead of every route sharing index.html's site-wide defaults.
// enabled: false skips every effect below — used when a page-level component (e.g. a quote
// calculator) is reused inside another page's layout (e.g. embedded on the Homepage) and must not
// override that page's own title/canonical/structured-data.
// rawTitle: when true, `title` is used verbatim as document.title with no " | Aeti Cleaning
// Services" suffix appended — for pages that need to control the exact browser-tab/search-result
// title text (e.g. the homepage's primary SEO title).
const useSeo = ({ title, description, path = "", structuredData = null, enabled = true, rawTitle = false }) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const fullTitle = !title ? SITE_NAME : rawTitle ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = `${SITE_URL}${path}`;

    document.title = fullTitle;
    if (description) {
      setMetaByName("description", description);
      setMetaByProperty("og:description", description);
      setMetaByName("twitter:description", description);
    }
    setCanonical(canonicalUrl);
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:url", canonicalUrl);
    setMetaByName("twitter:title", fullTitle);

    let script = null;
    if (structuredData) {
      script = document.getElementById(STRUCTURED_DATA_ID);
      if (!script) {
        script = document.createElement("script");
        script.id = STRUCTURED_DATA_ID;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    return () => {
      // Only this page's own structured data is ever injected here — remove it when the route
      // changes so a stale page's schema never lingers into the next one.
      if (script) script.remove();
    };
  }, [title, description, path, structuredData, enabled, rawTitle]);
};

export default useSeo;
